/**
 * PublicToolRoute
 * ---------------------------------------------------------------------------
 * Serves two audiences from the same address: a visitor sees the tool's
 * marketing page, a signed-in member gets the tool itself (with a single,
 * dismissible cross-sell nudge above it).
 */
import React, { ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { ToolLandingPage } from '@/components/tools/ToolLandingPage';
import { ToolCrossSell } from '@/components/tools/ToolCrossSell';
import { TOOL_LANDINGS, type ToolSlug } from '@/config/toolLandings';

interface PublicToolRouteProps {
  slug: ToolSlug;
  children: ReactNode;
  /** Some tools render their own full-bleed shell; skip the nudge there. */
  showCrossSell?: boolean;
}

export const PublicToolRoute: React.FC<PublicToolRouteProps> = ({
  slug,
  children,
  showCrossSell = true,
}) => {
  const { user, loading } = useAuth();
  const tool = TOOL_LANDINGS[slug];

  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return <ToolLandingPage tool={tool} />;
  }

  return (
    <>
      {showCrossSell && <ToolCrossSell slug={slug} />}
      {children}
    </>
  );
};

export default PublicToolRoute;
